import { useEffect, type MouseEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLocale, useT } from '@/i18n';
import { useSession } from '@/hooks/session';
import { startPathFor } from '@/features/auth/guest';
import { trackCta } from '@/lib/analytics';
import { ClinicalLead, SampleReport, Testimonials } from '@/features/proof/Proof';
import { AppShell } from '@/components/layout';
import {
  BandPill,
  Button,
  Heart,
  Illustration,
  Lines,
  Lock,
  Shield,
  Skeleton,
  WhatsApp,
  Check,
  Download,
} from '@/components/ui';
import { photo } from '@/lib/photos';
import { learnFor } from '@/content/learn/localized';
import { LANDING_FAQ } from '@/content/faq';
import { FaqList } from '@/features/faq/FaqList';
import { formatMoney, priceLabel } from '@/lib/format';
import { sceneImage } from '@/lib/illustrations';
import { ROOM_ORDER, ROOM_STYLE } from '@/lib/rooms';

const FREE_PAGE_COUNT = 2;
const HOW = [1, 2, 3] as const;
/** "Who it's for" chips, each opening its Learn page. */
const WHO = ['autism', 'adhd', 'learning-difficulties', 'speech-language-delay', 'developmental-delay', 'sensory-differences', 'tantrums-meltdowns'] as const;
const MOMENTS = [1, 2, 3, 4] as const;
const BRIDGE = ['before', 'between', 'next'] as const;
const LEARN_TEASER = ['late-talking', 'autism', 'caring-for-yourself'] as const;

const Section = ({ id, children, className = '' }: { id?: string; children: ReactNode; className?: string }) => (
  <section id={id} className={`mx-auto w-full max-w-content scroll-mt-24 px-4 py-14 md:px-6 nav:py-20 ${className}`}>
    {children}
  </section>
);

/** Free report vs the paid "detailed Home plan", row by row. Rows 1–2 are free; everything is in the paid plan. */
const COMPARE_ROWS = [1, 2, 3, 4, 5, 6, 7, 8] as const;
const FREE_ROWS = 2;

const PlanComparison = () => {
  const t = useT();
  const mark = (included: boolean) =>
    included ? (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-sage-100 text-sage-600">
        <Check className="h-4 w-4" />
        <span className="sr-only">{t('compare.included')}</span>
      </span>
    ) : (
      <span className="inline-flex h-7 w-7 items-center justify-center text-ink-muted">
        <span aria-hidden="true">–</span>
        <span className="sr-only">{t('compare.notIncluded')}</span>
      </span>
    );
  return (
    <section aria-labelledby="compare-h" className="mt-14">
      <h3 id="compare-h" className="text-h2">{t('compare.title')}</h3>
      <p className="mt-2 max-w-prose text-body text-ink-soft">{t('compare.sub')}</p>
      <div className="mt-6 overflow-hidden rounded-xl border border-line bg-white shadow-1">
        <table className="w-full border-collapse text-left">
          <thead>
            <tr className="bg-paper">
              <th scope="col" className="px-4 py-3 text-label text-ink-muted sm:px-6">{t('compare.feature')}</th>
              <th scope="col" className="w-[84px] px-2 py-3 text-center text-label text-ink sm:w-[150px]">{t('compare.free')}</th>
              <th scope="col" className="w-[96px] bg-lagoon-600 px-2 py-3 text-center text-label text-white sm:w-[170px]">{t('compare.paid')}</th>
            </tr>
          </thead>
          <tbody>
            {COMPARE_ROWS.map((n) => (
              <tr key={n} className="border-t border-line">
                <th scope="row" className="px-4 py-3.5 text-body font-normal text-ink sm:px-6">{t(`compare.row${n}`)}</th>
                <td className="px-2 py-3.5 text-center">{mark(n <= FREE_ROWS)}</td>
                <td className="bg-lagoon-100/40 px-2 py-3.5 text-center">{mark(true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

/** The public pages. The home page is a short overview; each header menu item opens its own page. */
export type LandingView = 'home' | 'homePlan' | 'how' | 'pricing';

/** Public path and nav label for each menu page, shared by the header, footer and router. */
export const PUBLIC_PAGES = [
  { view: 'homePlan', path: '/home-plan', key: 'nav.homePlan' },
  { view: 'how', path: '/how-it-works', key: 'nav.howItWorks' },
  { view: 'pricing', path: '/pricing', key: 'nav.pricing' },
] as const satisfies ReadonlyArray<{ view: LandingView; path: string; key: string }>;

const MOVED_SECTIONS: Record<string, string> = { '#peek': '/home-plan', '#how': '/how-it-works', '#rooms': '/home-plan#rooms', '#pricing': '/pricing' };

/** Old public paths, now one "Home plan" page (the report, then the plan it comes with). */
export const RETIRED_PUBLIC_PATHS: Record<string, string> = { '/the-report': '/home-plan', '/home-programme': '/home-plan#rooms' };

export const Landing = ({ view = 'home' }: { view?: LandingView }) => {
  const t = useT();
  const { locale } = useLocale();
  const home = view === 'home';
  const page = PUBLIC_PAGES.find((p) => p.view === view);
  const { hash } = useLocation();
  const { account, config } = useSession();
  const navigate = useNavigate();

  const ctaTo = startPathFor(account, config);
  const plusTo = account ? '/plus' : '/signup';
  const pricing = config?.pricing.IN;
  const pageTitles = config?.report.pageTitles;
  const unlockPlan = config?.plans.unlock;
  const plusPlan = config?.plans.plus;

  // Every CTA is a Button rendered as a single focusable <a> with a real href (open in new tab,
  // hover preview) that still routes through react-router for a plain left click.
  const makeCtaClick = (to: string, id: string) => (event: MouseEvent<HTMLElement>) => {
    // Counted on every kind of click (new tab too); `id` says which button, never where it led.
    trackCta(id);
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
  };

  // Old links pointed at sections of one long home page; send them to the section's own page.
  useEffect(() => {
    const moved = home ? MOVED_SECTIONS[hash] : undefined;
    if (moved) navigate(moved, { replace: true });
  }, [home, hash, navigate]);

  return (
    <AppShell wide>
      {page && (
        <div className="bg-mist">
          <div className="mx-auto max-w-content px-4 pb-2 pt-10 md:px-6 nav:pt-14">
            <h1 className="text-h1 text-ink">{t(page.key)}</h1>
          </div>
        </div>
      )}

      {home && (<>
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-40 -top-48 h-[640px] w-[640px] rounded-full bg-sage-100 nav:-right-24"
        />
        <div className="relative mx-auto grid max-w-content items-center gap-12 px-4 pb-12 pt-10 md:px-6 nav:grid-cols-[1.05fr_1fr] nav:pb-16 nav:pt-16">
          <div>
            <p className="inline-flex items-center gap-2 rounded-pill border-[1.5px] border-line bg-white py-1.5 pl-1.5 pr-4 text-label">
              <span className="rounded-pill bg-coral-100 px-2.5 py-0.5 text-caption text-coral-ink">{t('l2.chipTag')}</span>
              {t('l2.chip')}
            </p>
            <h1 className="mt-6 text-hero text-ink">{t('l2.headline')}</h1>
            <p className="mt-5 max-w-[34em] text-[18px] leading-relaxed text-ink-soft">{t('l2.sub')}</p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Button as="a" href={ctaTo} onClick={makeCtaClick(ctaTo, 'hero_start')} size="lg">
                {t('landing.cta')}
              </Button>
              <Link to="/#sample" onClick={(e) => { trackCta('hero_sample'); const el = document.getElementById('sample'); if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth' }); } }} className="inline-flex min-h-tap items-center px-2 text-label font-extrabold text-lagoon-600 underline-offset-4 hover:underline">
                {t('l2.sample')} →
              </Link>
            </div>
            <dl className="mt-9 flex flex-wrap gap-x-8 gap-y-3 text-label text-ink-soft">
              {([1, 2, 3] as const).map((n) => (
                <div key={n}>
                  <dt className="font-display text-[22px] font-extrabold leading-tight text-ink">{t(`l2.fact${n}`)}</dt>
                  <dd>{t(`l2.fact${n}b`)}</dd>
                </div>
              ))}
            </dl>
            <div aria-hidden="true" className="mt-10 flex justify-center nav:hidden">
              <div className="h-[300px] w-[240px] overflow-hidden rounded-arch bg-white shadow-[0_0_0_8px_#8b5cf6]">
                <Illustration src={photo('landing-hero')} alt="" position="50% 30%" eager />
              </div>
            </div>
          </div>

          {/* Decorative composition: the logo's embrace as an arch around a real illustration. */}
          <div aria-hidden="true" className="relative mx-auto hidden h-[500px] w-full max-w-[520px] nav:block">
            <div className="absolute left-[40px] top-[14px] h-[472px] w-[372px] overflow-hidden rounded-arch bg-white shadow-[0_0_0_10px_#8b5cf6,0_30px_60px_-24px_rgba(29,78,216,.45)]">
              <Illustration src={photo('landing-hero')} alt="" position="50% 30%" eager />
            </div>
            <div className="absolute left-0 top-[96px] flex h-14 w-14 -rotate-[10deg] items-center justify-center rounded-[18px] bg-coral-500 shadow-coral">
              <Heart className="h-7 w-7 fill-white text-white" />
            </div>
            <div className="absolute -right-2 bottom-2 w-[280px] rounded-lg bg-white p-4 shadow-2">
              <p className="eyebrow text-ink-muted">{t('l2.float.title')}</p>
              <div className="mt-2 divide-y divide-line text-label">
                <p className="flex items-center justify-between gap-2 py-2">{t('l2.areaA')}<BandPill band="ok" /></p>
                <p className="flex items-center justify-between gap-2 py-2">{t('l2.areaB')}<BandPill band="watch" /></p>
              </div>
              <p className="mt-1 text-right text-[11px] font-bold text-ink-muted">{t('l2.example')}</p>
            </div>
            <div className="absolute -right-2 top-4 w-[230px] rotate-[2deg] rounded-lg bg-white p-4 shadow-2">
              <div className="flex items-center justify-between">
                <p className="font-display text-[17px] font-bold leading-tight">{t('l2.pdf.title')}</p>
                <span className="rounded-sm bg-coral-100 px-2 py-0.5 text-[11px] font-extrabold text-coral-ink">PDF</span>
              </div>
              <p className="text-caption text-ink-muted">{t('l2.pdf.meta')}</p>
              <div className="mt-3 rounded-md bg-paper p-3">
                <p className="text-[12px] font-extrabold text-ink">{t('l2.pdf.page')}</p>
                <span className="mt-2 block h-1.5 rounded-pill bg-line" />
                <span className="mt-1.5 block h-1.5 w-4/5 rounded-pill bg-line" />
                <span className="mt-1.5 block h-1.5 w-3/5 rounded-pill bg-line" />
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-content px-4 md:px-6">
          <ul className="grid gap-4 rounded-lg bg-white px-6 py-5 text-label text-ink-soft shadow-1 sm:grid-cols-2 nav:grid-cols-4">
            {[
              [Shield, 'l2.trust0'],
              [Lines, 'l2.trust1'],
              [Lock, 'l2.trust2'],
              [WhatsApp, 'l2.trust3'],
            ].map(([Icon, key]) => {
              const I = Icon as typeof Lines;
              return (
                <li key={key as string} className="flex items-center gap-3">
                  <I className="h-6 w-6 shrink-0 text-lagoon-600" />
                  {t(key as string)}
                </li>
              );
            })}
          </ul>
          {pricing ? (
            <p className="mt-4 text-center text-caption text-ink-muted">
              {t('landing.free', { price: priceLabel(pricing) })}{' '}
              <s>{t('checkout.list', { price: formatMoney(pricing.listAmount, pricing.currency) })}</s>
            </p>
          ) : (
            <Skeleton lines={1} className="mx-auto mt-4 max-w-prose" />
          )}
        </div>
      </div>
      </>)}

      {home && (<>
      {/* The report itself, one tap from the first screen, and who stands behind it */}
      <SampleReport from="home" />
      <ClinicalLead />
      </>)}

      {home && (<>
      {/* Who it's for: parents recognise themselves in two seconds */}
      <div className="bg-white">
        <Section id="who" className="grid items-start gap-8 nav:grid-cols-[1fr_1.35fr]">
          <div>
            <p className="eyebrow text-sage-600">{t('l3.who.eyebrow')}</p>
            <h2 className="mt-2 text-display">{t('l3.who.title')}</h2>
            <p className="mt-3 max-w-[30em] text-[17px] text-ink-soft">{t('l3.who.body')}</p>
          </div>
          <ul className="flex flex-wrap gap-2.5 nav:pt-10">
            {WHO.map((slug) => (
              <li key={slug}>
                <Link
                  to={`/learn/${slug}`}
                  className="inline-flex min-h-[48px] items-center gap-2 rounded-pill border-[1.5px] border-sage-300 bg-paper px-5 text-label text-ink transition-colors duration-fade hover:border-lagoon-500 hover:bg-white hover:text-lagoon-700"
                >
                  {t(`l3.who.${slug}`)}
                </Link>
              </li>
            ))}
            <li>
              <a
                href={ctaTo}
                onClick={makeCtaClick(ctaTo, 'who_unsure')}
                className="inline-flex min-h-[48px] items-center gap-2 rounded-pill bg-coral-100 px-5 text-label text-coral-ink hover:bg-coral-300/60"
              >
                {t('l3.who.unsure')} →
              </a>
            </li>
          </ul>
        </Section>
      </div>

      {/* Sound familiar? The moments parents live with, each answered by what Nurture does */}
      <Section id="familiar">
        <div className="grid gap-4 nav:grid-cols-[1fr_1.6fr] nav:items-end">
          <h2 className="text-display">{t('l3.familiar.title')}</h2>
          <p className="max-w-[36em] text-[17px] text-ink-soft">{t('l3.familiar.sub')}</p>
        </div>
        <ul className="mt-9 grid gap-4 md:grid-cols-2">
          {MOMENTS.map((n) => (
            <li key={n} className={`flex flex-col rounded-xl p-6 sm:p-7 ${n === 1 || n === 4 ? 'bg-white shadow-1' : 'border border-line bg-white/70'}`}>
              <p className="font-display text-[23px] font-bold leading-snug text-ink">
                <span aria-hidden="true" className="text-sage-500">“</span>
                {t(`l3.m${n}.quote`)}
                <span aria-hidden="true" className="text-sage-500">”</span>
              </p>
              <p className="mt-4 flex items-start gap-3 border-t border-line pt-4 text-body text-ink-soft">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-lagoon-600" />
                {t(`l3.m${n}.answer`)}
              </p>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-caption text-ink-muted">{t('l3.familiar.note')}</p>
      </Section>
      </>)}

      {view === 'homePlan' && (<>
      <SampleReport from="homeplan" />
      </>)}

      {view === 'homePlan' && (<>
      {/* The tailored report — the product's core — then the home plan it comes with */}
      <div className="bg-white">
        <Section id="peek" className="grid items-center gap-12 nav:grid-cols-[1fr_1.05fr]">
          <div>
            <p className="eyebrow text-coral-600">{t('homePlan.reportEyebrow')}</p>
            <h2 className="mt-2 text-display">{t('l2.peek.title')}</h2>
            <p className="mt-3 max-w-[36em] text-[17px] text-ink-soft">{t('l2.peek.body')}</p>
            <ul className="mt-6 flex flex-col gap-3">
              {(['l2.peek.point1', 'l2.peek.point2', 'l2.peek.point3'] as const).map((key) => (
                <li key={key} className="flex items-start gap-3 text-body text-ink">
                  <span aria-hidden="true" className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sage-100 text-sage-600">
                    <Check className="h-4 w-4" />
                  </span>
                  {t(key)}
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Button as="a" href={ctaTo} onClick={makeCtaClick(ctaTo, 'bridge_start')}>
                {t('landing.cta')}
              </Button>
            </div>
          </div>
          <div className="relative">
            <div aria-hidden="true" className="absolute -right-3 top-6 hidden h-full w-full rotate-[3deg] rounded-lg bg-sage-200 sm:block" />
            <div className="relative rounded-lg border border-line bg-paper p-6 shadow-2 sm:p-8">
              <div className="flex items-center justify-between gap-3 border-b border-line pb-4">
                <div className="flex items-center gap-3">
                  <img src="/brand/logo-mark.png" alt="" className="h-10 w-10" />
                  <div>
                    <p className="font-display text-[20px] font-bold leading-tight">{t('l2.pdf.title')}</p>
                    <p className="text-caption text-ink-muted">{t('l2.pdf.meta')}</p>
                  </div>
                </div>
                <span className="inline-flex items-center gap-1.5 rounded-sm bg-coral-100 px-2.5 py-1 text-caption font-extrabold text-coral-ink">
                  <Download className="h-4 w-4" />
                  PDF
                </span>
              </div>
              <h3 className="mb-3 mt-5 text-label text-ink-muted">{t('landing.receive')}</h3>
              <ol className="grid gap-2">
                {pageTitles
                  ? pageTitles.map((title, index) => (
                      <li key={title} className="flex min-h-[52px] items-center justify-between gap-3 rounded-md bg-white px-4 py-2.5 shadow-1">
                        <span className="flex items-center gap-3 text-body text-ink">
                          <span className="font-display text-[18px] font-extrabold text-lagoon-500">{index + 1}</span>
                          {title}
                        </span>
                        {index >= FREE_PAGE_COUNT && (
                          <span className="flex shrink-0 items-center gap-1.5 text-caption text-coral-ink">
                            <Lock className="h-4 w-4" />
                            {t('report.lockedList')}
                          </span>
                        )}
                      </li>
                    ))
                  : Array.from({ length: 6 }, (_, index) => (
                      <li key={index} className="rounded-md bg-white p-4">
                        <Skeleton lines={1} />
                      </li>
                    ))}
              </ol>
            </div>
          </div>
        </Section>
      </div>
      </>)}

      {home && <Testimonials />}

      {home && (<>
      {/* A bridge between visits */}
      <div className="bg-mist">
        <Section id="bridge" className="grid items-center gap-10 nav:grid-cols-[300px_1fr]">
          <div aria-hidden="true" className="mx-auto h-[240px] w-[240px] overflow-hidden rounded-full border-[6px] border-white shadow-2 nav:h-[300px] nav:w-[300px]">
            <Illustration src={photo('landing-bridge')} alt="" position="50% 35%" />
          </div>
          <div>
            <p className="eyebrow text-lagoon-600">{t('l3.bridge.eyebrow')}</p>
            <h2 className="mt-2 text-display">{t('l3.bridge.title')}</h2>
            <p className="mt-3 max-w-[40em] text-[17px] text-ink-soft">{t('l3.bridge.body')}</p>
            <ol className="mt-8 grid gap-6 sm:grid-cols-3">
              {BRIDGE.map((k, i) => (
                <li key={k} className="border-t-[3px] border-lagoon-200 pt-3">
                  <span className="font-display text-[15px] font-extrabold tabular-nums text-lagoon-600">0{i + 1}</span>
                  <h3 className="mt-1 text-h3">{t(`l3.bridge.${k}.title`)}</h3>
                  <p className="mt-1 text-body text-ink-soft">{t(`l3.bridge.${k}.body`)}</p>
                </li>
              ))}
            </ol>
          </div>
        </Section>
      </div>
      </>)}

      {view === 'how' && (<>
      {/* How it works */}
      <Section id="how">
        <h2 className="text-display">{t('landing.how')}</h2>
        <ol className="relative mt-10 grid gap-10 nav:grid-cols-3">
          <span aria-hidden="true" className="absolute left-10 right-10 top-[34px] hidden border-t-[3px] border-dashed border-sage-300 nav:block" />
          {HOW.map((n) => (
            <li key={n} className="relative">
              <span
                aria-hidden="true"
                className={`flex h-[68px] w-[68px] items-center justify-center rounded-[24px] border-[3px] bg-paper font-display text-[26px] font-extrabold text-lagoon-600 ${n === 3 ? 'border-coral-500' : 'border-sage-500'}`}
              >
                {n}
              </span>
              <h3 className="mt-4 text-h3">{t(`l2.how${n}.title`)}</h3>
              <p className="mt-1 max-w-[24em] text-body text-ink-soft">{t(`l2.how${n}.body`)}</p>
            </li>
          ))}
        </ol>
      </Section>
      </>)}

      {view === 'homePlan' && (<>
      {/* Rooms: the home plan that comes with the full report */}
      <Section id="rooms">
        <p className="eyebrow text-lagoon-500">{t('homePlan.planEyebrow')}</p>
        <h2 className="mt-2 text-display">{t('l2.rooms.title')}</h2>
        <p className="mt-3 max-w-[40em] text-[17px] text-ink-soft">{t('l2.rooms.body')}</p>
        <ul className="mt-8 grid gap-4 sm:grid-cols-2 nav:grid-cols-[1.4fr_1fr_1fr] nav:grid-rows-[210px_210px]">
          {ROOM_ORDER.map((room, i) => (
            <li key={room} className={`relative min-h-[200px] overflow-hidden rounded-xl bg-white shadow-1 ${i === 0 ? 'nav:row-span-2' : ''}`}>
              <Illustration src={sceneImage(ROOM_STYLE[room].scene)} alt="" className="absolute inset-0" position="50% 40%" />
              <div className="absolute inset-x-3 bottom-3 rounded-lg bg-white/95 px-4 py-3">
                <h3 className="text-h3">{t(`room.${room}`)}</h3>
                <p className="text-caption text-ink-soft">{t(`room.hint.${room}`)}</p>
              </div>
            </li>
          ))}
        </ul>
      </Section>
      </>)}

      {home && (<>
      {/* Private by design */}
      <div className="bg-ink text-white">
        <Section className="grid items-center gap-8 nav:grid-cols-[1fr_1.5fr]">
          <div>
            <Lock className="h-8 w-8 text-lagoon-200" />
            <h2 className="mt-4 text-display text-white">{t('l3.private.title')}</h2>
          </div>
          <div>
            <ul className="grid gap-3 sm:grid-cols-2">
              {([1, 2, 3, 4] as const).map((n) => (
                <li key={n} className="flex items-start gap-3 text-body text-lagoon-100">
                  <Check className="mt-0.5 h-5 w-5 shrink-0 text-lagoon-200" />
                  {t(`l3.private.${n}`)}
                </li>
              ))}
            </ul>
            <Link to="/trust" className="mt-6 inline-flex min-h-tap items-center text-label font-extrabold text-white underline-offset-4 hover:underline">
              {t('l3.private.link')} →
            </Link>
          </div>
        </Section>
      </div>
      </>)}

      {view === 'pricing' && (<>
      {/* Pricing */}
      <div className="bg-white">
        <Section id="pricing">
          <h2 className="text-display">{t('landing.plans')}</h2>
          <p className="mt-3 max-w-prose text-[17px] text-ink-soft">{t('landing.plans.free')}</p>
          <div className={`mt-8 grid items-stretch gap-5 ${plusPlan ? 'nav:grid-cols-3' : 'md:grid-cols-2'}`}>
            {config ? (
              <div className="flex flex-col gap-3 rounded-xl border-[1.5px] border-line bg-white p-7">
                <h3 className="text-h3">{t('compare.free')}</h3>
                <p className="text-body text-ink-soft">{t('compare.freeBody')}</p>
                <p className="font-display text-[46px] font-extrabold leading-none">{t('compare.freePrice')}</p>
                <Button as="a" href={ctaTo} onClick={makeCtaClick(ctaTo, 'pricing_free')} variant="secondary" full className="mt-auto">
                  {t('landing.cta')}
                </Button>
              </div>
            ) : (
              <div className="rounded-xl bg-paper p-7">
                <Skeleton lines={4} />
              </div>
            )}
            {config && unlockPlan ? (
              <div className="flex flex-col gap-3 rounded-xl bg-lagoon-600 p-7 text-white shadow-2">
                <h3 className="text-h3 text-white">{t('plan.oneTime.title')}</h3>
                <p className="text-body text-[#bfdbfe]">{t('plan.oneTime.body', { n: config.entitlement.regenerationsPerPurchase, days: config.entitlement.windowDays })}</p>
                <p className="font-display text-[46px] font-extrabold leading-none">
                  {formatMoney(unlockPlan.launchAmount, unlockPlan.currency)}
                  {unlockPlan.listAmount > unlockPlan.launchAmount && (
                    <s className="ml-3 align-middle text-[18px] font-semibold opacity-60">
                      {t('checkout.list', { price: formatMoney(unlockPlan.listAmount, unlockPlan.currency) })}
                    </s>
                  )}
                </p>
                {unlockPlan.taxInclusive ? (
                  <p className="text-caption text-[#bfdbfe]">{t('plan.gst')}</p>
                ) : (
                  (unlockPlan.taxAmount ?? 0) > 0 && <p className="text-caption text-[#bfdbfe]">{t('plan.taxExtra', { label: unlockPlan.taxLabel })}</p>
                )}
                <Button as="a" href={ctaTo} onClick={makeCtaClick(ctaTo, 'pricing_unlock')} variant="celebrate" full className="mt-auto">
                  {t('landing.cta')}
                </Button>
              </div>
            ) : (
              <div className="rounded-xl bg-paper p-7">
                <Skeleton lines={4} />
              </div>
            )}

            {!config ? (
              <div className="rounded-xl bg-paper p-7">
                <Skeleton lines={4} />
              </div>
            ) : (
              plusPlan && (
                <div className="flex flex-col gap-3 rounded-xl bg-paper p-7 shadow-1">
                  <h3 className="text-h3">{t('plan.plus.title')}</h3>
                  <p className="text-body text-ink-soft">
                    {t('plan.plus.body', { n: plusPlan.unlocksPerMonth, price: formatMoney(plusPlan.amount, plusPlan.currency) })}
                  </p>
                  <Button as="a" href={plusTo} onClick={makeCtaClick(plusTo, 'pricing_plus')} variant="secondary" full className="mt-auto">
                    {t('plan.plus.cta')}
                  </Button>
                </div>
              )
            )}
          </div>

          <PlanComparison />
        </Section>
      </div>
      </>)}

      {(home || view === 'pricing') && (<>
      {/* FAQ: questions left, answers open in place */}
      <Section className="grid gap-8 nav:grid-cols-[1fr_1.6fr]">
        <div>
          <h2 className="text-display">{t('landing.faq')}</h2>
          <p className="mt-3 max-w-[26em] text-body text-ink-soft">{t('l2.faqIntro')}</p>
        </div>
        <div>
          <FaqList ids={LANDING_FAQ} />
          <Link to="/faq" className="mt-5 inline-flex min-h-tap items-center text-label font-extrabold text-lagoon-600 underline-offset-4 hover:underline">
            {t('l3.faq.all')} →
          </Link>
        </div>
      </Section>
      </>)}

      {home && (<>
      {/* Learn teaser */}
      <div className="bg-white">
        <Section id="learn">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="eyebrow text-sage-600">{t('nav.learn')}</p>
              <h2 className="mt-2 text-display">{t('l3.learn.title')}</h2>
              <p className="mt-2 max-w-[36em] text-[17px] text-ink-soft">{t('l3.learn.body')}</p>
            </div>
            <Link to="/learn" className="inline-flex min-h-tap items-center text-label font-extrabold text-lagoon-600 underline-offset-4 hover:underline">
              {t('l3.learn.all')} →
            </Link>
          </div>
          <ul className="mt-8 grid gap-4 md:grid-cols-[1.3fr_1fr_1fr]">
            {LEARN_TEASER.map((slug, i) => {
              const a = learnFor(locale).find((x) => x.slug === slug)!;
              return (
                <li key={slug}>
                  <Link to={`/learn/${slug}`} className="lift group flex h-full flex-col overflow-hidden rounded-lg bg-paper">
                    <div className={`shrink-0 ${i === 0 ? 'h-[200px]' : 'h-[150px]'}`}>
                      <Illustration src={photo(a.photo)} alt="" position="50% 40%" />
                    </div>
                    <span className="flex flex-1 flex-col gap-1 p-5">
                      <span className="text-h3 text-ink group-hover:text-lagoon-700">{a.title}</span>
                      <span className="text-body text-ink-soft">{a.summary}</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </Section>
      </div>
      </>)}

      <div className="mx-auto my-14 max-w-content px-4 md:px-6">
        <div className="grid overflow-hidden rounded-xl bg-coral-100 nav:grid-cols-[1.2fr_1fr]">
          <div className="flex flex-col items-start gap-5 px-6 py-10 nav:px-12">
            <p className="font-display text-h1 font-extrabold text-ink">{t('l2.final')}</p>
            <p className="max-w-[30em] text-body text-ink-soft">{t('l3.final.body')}</p>
            <Button as="a" href={ctaTo} onClick={makeCtaClick(ctaTo, 'closing_start')} size="lg">
              {t('landing.cta')}
            </Button>
          </div>
          <div aria-hidden="true" className="hidden min-h-[280px] nav:block">
            <Illustration src={photo('landing-final')} alt="" position="50% 40%" />
          </div>
        </div>
      </div>
    </AppShell>
  );
};
